import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { HardDrives, PencilSimple, CaretDown, CaretUp } from "@phosphor-icons/react";

const fmt = (b) => {
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(2)} GB`;
  if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.round(b / 1024)} KB`;
};

export const StorageMeterCard = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [editQuota, setEditQuota] = useState(false);
  const [quotaInput, setQuotaInput] = useState("");
  const [showLessons, setShowLessons] = useState(false);

  const load = () => api.get("/admin/storage/usage").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const saveQuota = async () => {
    try {
      await api.post("/admin/storage/quota", { quota_gb: parseFloat(quotaInput) });
      setEditQuota(false); load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  if (err) return <div className="card mb-4 text-sm text-[#FF5100]" data-testid="storage-meter-error">{err}</div>;
  if (!data) return <div className="card mb-4 text-sm text-[#000000]" data-testid="storage-meter-loading">Measuring storage…</div>;

  const pct = Math.min(100, (data.used_bytes / data.quota_bytes) * 100);
  const left = Math.max(0, data.quota_bytes - data.used_bytes);
  const barColor = pct >= 90 ? "#DC2626" : pct >= 70 ? "#F59E0B" : "#CCFF00";

  return (
    <div className="card mb-4" data-testid="storage-meter-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h2 className="text-lg font-bold font-display flex items-center gap-1.5">
          <HardDrives size={18} color="#00B8D4" /> Storage
        </h2>
        <div className="text-sm text-[#000000] flex items-center gap-1.5" data-testid="storage-quota">
          {editQuota ? (
            <>
              <input type="number" step="0.5" min="0.1" className="input-base text-sm w-20" style={{ padding: "4px 8px" }}
                value={quotaInput} onChange={(e) => setQuotaInput(e.target.value)} data-testid="storage-quota-input" />
              <span>GB</span>
              <button className="btn-secondary text-xs" style={{ padding: "4px 10px" }} onClick={saveQuota} data-testid="storage-quota-save">Save</button>
              <button className="btn-ghost text-xs" onClick={() => setEditQuota(false)}>Cancel</button>
            </>
          ) : (
            <>
              <span>Plan: <strong>{data.quota_gb} GB</strong></span>
              <button className="btn-ghost p-1" title="Update total storage if your plan changes"
                onClick={() => { setQuotaInput(String(data.quota_gb)); setEditQuota(true); }} data-testid="storage-quota-edit">
                <PencilSimple size={13} />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="w-full h-3 rounded-full bg-[#ECEDEF] overflow-hidden" data-testid="storage-meter-bar">
        <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: barColor }} />
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2 mt-1.5 text-sm">
        <span data-testid="storage-used"><strong>{fmt(data.used_bytes)}</strong> used ({pct.toFixed(1)}%)</span>
        {data.used_bytes > data.quota_bytes ? (
          <span className="text-[#DC2626] font-semibold" data-testid="storage-left">{fmt(data.used_bytes - data.quota_bytes)} OVER plan</span>
        ) : (
          <span className="text-[#000000]" data-testid="storage-left"><strong>{fmt(left)}</strong> left</span>
        )}
      </div>
      <div className="flex gap-2 flex-wrap mt-2 text-[11px] text-[#000000]" data-testid="storage-breakdown">
        <span className="px-2 py-0.5 rounded-full bg-[#ECEDEF]">Videos {fmt(data.breakdown.videos)}</span>
        <span className="px-2 py-0.5 rounded-full bg-[#ECEDEF]">Images {fmt(data.breakdown.images)}</span>
        <span className="px-2 py-0.5 rounded-full bg-[#ECEDEF]">Documents {fmt(data.breakdown.documents)}</span>
        {data.breakdown.other > 0 && <span className="px-2 py-0.5 rounded-full bg-[#ECEDEF]">Other {fmt(data.breakdown.other)}</span>}
      </div>
      {data.per_lesson.length > 0 && (
        <div className="mt-3">
          <button className="text-xs font-semibold text-[#00768a] flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0"
            onClick={() => setShowLessons((s) => !s)} data-testid="storage-lessons-toggle">
            {showLessons ? <CaretUp size={12} /> : <CaretDown size={12} />} Storage per lesson ({data.per_lesson.length})
          </button>
          {showLessons && (
            <div className="mt-2 max-h-64 overflow-y-auto pr-1" data-testid="storage-lessons-list">
              {data.per_lesson.map((l) => (
                <div key={l.id} className="flex items-center justify-between gap-2 text-xs py-1 border-b border-[#ECEDEF] last:border-0">
                  <span className="truncate text-[#000000]">{l.title}</span>
                  <span className="text-[#000000] shrink-0">{fmt(l.bytes)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
